import React, { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { TransferSelector } from '@bingads-webui-react/transfer-selector';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Callout } from '@fluentui/react';
import { MaxInsertionOrderLimit } from '../constants';
import { useInsertionOrderData } from '../hooks/use-insertion-order-data';
import { getInsertionOrderSelectorStyle } from '../styles';
import { getSearchBarPlaceholder, getSearchFieldOptions } from '../utils';

export function InsertionOrderSelectorCallout({
  target,
  initialSelectedItems,
  onApply: onApplyProps,
  onDismiss,
}) {
  const classes = useDefaultStyles(getInsertionOrderSelectorStyle);
  const i18n = useContext(I18nContext);

  const [selectedInsertionOrderItems, setSelectedInsertionOrderItems] = useState(initialSelectedItems);
  const [queryText, setQueryText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [queryField, setQueryField] = useState(null);
  const [shouldAddAll, setShouldAddAll] = useState(true);

  const handleQueryChange = (newValue) => {
    setQueryText(newValue);
  };
  const handleClearSearch = () => {
    setQueryText('');
  };
  const onSearch = ({ searchText, searchField }) => {
    setShouldAddAll(false);
    setSearchTerm(searchText);
    setQueryField(searchField);
  };
  const searchProps = useMemo(() => {
    const placeholder = getSearchBarPlaceholder({ i18n, searchField: queryField });
    return {
      showSearchBar: true,
      onSearch,
      placeholder,
      queryText,
      handleQueryChange,
      handleClearSearch,
      showSearchFieldSelect: true,
      searchFieldOptions: getSearchFieldOptions(),
    };
  }, [i18n, queryField, queryText]);

  const { insertionOrderItems, total, loading } = useInsertionOrderData({
    i18n,
    searchTerm,
    queryField,
  });

  const onAddAll = useCallback(() => {
    const newSelected = _.map(insertionOrderItems, item => ({ ...item, selected: true }));
    setSelectedInsertionOrderItems(newSelected.toReversed());
  }, [insertionOrderItems]);

  useEffect(() => {
    if (shouldAddAll && _.isEmpty(initialSelectedItems) && !_.isEmpty(insertionOrderItems)) {
      onAddAll();
    }
  }, [initialSelectedItems, insertionOrderItems, onAddAll, shouldAddAll]);

  const onRemoveAll = () => {
    setSelectedInsertionOrderItems([]);
  };
  const onAdd = (selected) => {
    const newSelected = [selected, ...selectedInsertionOrderItems];
    setSelectedInsertionOrderItems(newSelected);
  };
  const onRemove = (selected) => {
    const newSelected = _.filter(selectedInsertionOrderItems, item => item.id !== selected.id);
    setSelectedInsertionOrderItems(newSelected);
  };

  const onApply = () => {
    onApplyProps(selectedInsertionOrderItems);
  };

  return (
    <Callout
      className={classes['scheduler-modal']}
      target={target}
      onDismiss={onDismiss}
      isBeakVisible={false}
    >
      <TransferSelector
        className={classnames(classes.MultipleInsertionOrderSelector, 'insertion-order-report-transfer-selector')}
        i18n={i18n}
        allItems={insertionOrderItems}
        searchProps={searchProps}
        selectedItems={selectedInsertionOrderItems}
        onRemoveAll={onRemoveAll}
        onAddAll={onAddAll}
        onRemove={onRemove}
        onAdd={onAdd}
        onApply={onApply}
        onDismissCallback={onDismiss}
        isBeyondAccountLimit={total > MaxInsertionOrderLimit}
        isLoadingItems={loading}
      />
    </Callout>
  );
}

InsertionOrderSelectorCallout.propTypes = {
  target: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  initialSelectedItems: PropTypes.arrayOf(PropTypes.shape({})),
};

InsertionOrderSelectorCallout.defaultProps = {
  initialSelectedItems: [],
};
